<template>
  <div class="dass-title-shipping">
    <b>{{ $t('etapas.transporte.titulo') }}:</b>
  </div>

  <div v-if="showCepInput" class="shipping-step-section cep-step-container">
    <div class="title-wrapper">
      <dass-tooltip
      type="span"
      :text="$t('etapas.transporte.confirmZipCodeTooltip')">
        <p class="step-title">
          {{ $t('etapas.transporte.confirmZipCode')}}:
        </p>
      </dass-tooltip>
    </div>
    <div class="cep-action-group">
      <div class="cep-input-wrapper">
        <dass-input-text
          type="tel"
          id="shipping-input-cep"
          v-model="cepClient"
          v-mask="'#####-###'"
          :label="$t('etapas.comprador.cep')"
          :disabled="!isEditingCep || isLoading"
        />
      </div>
      <dass-button 
        @click="handleCepAction" 
        :disabled="isLoading"
        variant="tertiary"
        class="cep-btn"
      >
        {{ isEditingCep ? $t('etapas.transporte.search') : $t('etapas.transporte.modify') }}
      </dass-button>
    </div>
  </div>

  <div v-if="isLoading" class="loading-container">
    <dass-spinner size="40" />
  </div>
  
  <div v-else class="shipping-step-section">
    <p v-if="showCepInput" class="step-title">{{ $t('etapas.transporte.selectReturnType')}}:</p>
    <div class="carriers-cards-container">
      <div 
        v-for="(carrier, index) in carriers" 
        :key="carrier.TRA_NOME" 
        class="carrier-card"
        :class="{ 'selected': selectedCarrier?.TRA_NOME === carrier.TRA_NOME }"
        @click="selectCarrier(carrier)"
      >
        <div class="radio-wrapper">
          <input
            :id="`carrier-radio-${index}`"
            type="radio"
            :value="carrier"
            :checked="selectedCarrier?.TRA_NOME === carrier.TRA_NOME"
            readonly
          />
        </div>
        <div class="card-info">
          <div class="card-header">
            <div class="title-with-icon">
              <img 
                v-if="getCarrierIcon(carrier.TRA_NOME)" 
                :src="getCarrierIcon(carrier.TRA_NOME)" 
                class="carrier-small-icon" 
                alt=""
              />
              <strong>{{ carrier.TRA_NOME }}</strong>
            </div>
          </div>
          <p class="card-description">
            {{ formatCarrierDescription(carrier.TRA_NOME) }}
          </p>
        </div>
      </div>
    </div>
    <transition name="fade">
      <div v-if="selectedCarrierRequiresAgency" class="agencies-wrapper">
        <div class="agencies-header">
          <b>{{ $t('etapas.transporte.selectDropOffPoint', {carrier: selectedCarrier.TRA_NOME, cep: lastSearchedCep}) }}:</b>
        </div>
        <div class="agencies-list">
          <div 
            v-for="(agency, index) in agenciesProximas" 
            :key="agency.code"
            class="agency-card"
            :class="{ 'selected': selectedAgency?.code === agency.code }"
            @click="selectAgency(agency)"
          >
            <div class="agency-radio-col">
              <input 
                type="radio" 
                :checked="selectedAgency?.code === agency.code"
                readonly
              />
            </div>
            <div class="agency-info-col">
              <div class="info-text-wrapper">
                <strong class="agency-name">{{ agency.name }}</strong>
                <small class="agency-address">
                  {{ $t('etapas.comprador.endereco') }}: {{ agency.END_ENDERECO }}, {{ agency.END_NUMERO }}
                </small>
                <small class="agency-address">
                  {{ $t('etapas.comprador.bairro') }}: {{ agency.END_BAIRRO }}
                </small>
                <small class="agency-address">
                  {{ $t('etapas.comprador.cidade') }}: {{ agency.END_CIDADE }}/{{ agency.END_UF }}
                </small>
                <small class="agency-address">
                  {{ $t('etapas.comprador.cep') }}: {{ agency.END_CEP }}
                </small>
                <div class="agency-badges">
                  <span class="badge dist">📍 {{ formatDistance(agency.distanciaAprox) }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="distance-disclaimer">
          <i>*{{ $t('etapas.transporte.approximateDistance') }}</i> 
        </div>
      </div>
    </transition>
  </div>
  <hr>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getOnlyNumbersFromString } from '../../helpers/normalizers/getOnlyNumbers.js'

export default {
  name: 'ShippingData',
  data() {
    return {
      carriers: [],
      selectedCarrier: null,
      isLoading: false,
      agenciesCache: {},
      rawAgencies: [],
      agenciesProximas: [],
      selectedAgency: null,
      rangeParam: 10,
      cepClient: '',
      latitudeClient: '',
      longitudeClient: '',
      agencyCarriersConfig: ['JADLOG'],
      showCepInput: false,
      isEditingCep: false,
      lastSearchedCep: '',
    }
  },

  computed: {
    ...mapGetters('create_protocol', {dados_item: 'getDadosItens'}),
    ...mapGetters('brand', {brand: 'getBrand'}),
    itens() {     
      return Array.isArray(this.dados_item) ? this.dados_item : [this.dados_item]
    },
    selectedCarrierRequiresAgency() {
      if (!this.selectedCarrier || !this.selectedCarrier.TRA_NOME) return false
      const name = this.selectedCarrier.TRA_NOME.toUpperCase()
      return this.agencyCarriersConfig.some(configName => name.includes(configName))
    },
  },

  methods: {
    ...mapActions('addressBook', { getDataCeps: 'getDataCeps' }),
    ...mapActions('create_protocol', [
      'loadCarriersReverse',
      'setShippingData',
      'resetShippingData',
      'loadCarriersAgencies',
      'loadGeoCoordinates'
    ]),

    getCarrierIcon(name) {
      if (!name) return null
      const imageName = `${name.toLowerCase().trim()}-icon.png`
      try {
        return require(`@/assets/${imageName}`)
      } catch (e) {
        return null
      }
    },

    async handleCepAction() {
      if (this.isEditingCep) {
        const cleanCep = getOnlyNumbersFromString(this.cepClient || '')
        if (cleanCep.length !== 8) {
          this.$ShowAlert({
            variant: 'warning',
            message: this.$t('dass.invalidCep'),
            hideDelay: 2500
          })
          return
        }
        this.isLoading = true
        try {
          const data = await this.getDataCeps(cleanCep)
          if (data && data.length > 0) {
            if (cleanCep !== this.lastSearchedCep) {
              await this.fetchCarriersAndAgencies()
            }
            this.isEditingCep = false
          } else {
            this.cepClient = this.lastSearchedCep
            this.$ShowAlert({
              variant: 'error',
              message: this.$t('dass.zipCodeNotFound'),
              hideDelay: 2000
            })
          }
        } catch (error) {
          this.cepClient = this.lastSearchedCep;
          this.$ShowAlert({
            variant: 'error',
            message: this.$t('dass.zipCodeNotFound'),
            hideDelay: 2000
          })
        } finally {
          this.isLoading = false
        }
      } else {
        this.isEditingCep = true
      }
    },

    formatCarrierDescription(name) {
      if (!name) return ''
      const upperName = name.toUpperCase()
      if (upperName.includes('PAC')) return this.$t('etapas.transporte.descriptionPac')
      if (upperName.includes('JADLOG')) return this.$t('etapas.transporte.descriptionJadlog')
    },

    selectCarrier(carrier) {
      this.resetShippingData()
      this.selectedCarrier = carrier
      this.selectedAgency = null
      this.agenciesProximas = []

      if (this.selectedCarrierRequiresAgency) {
        const carrierName = carrier.TRA_NOME
        const agencies = this.agenciesCache[carrierName] || []
        if (agencies.length > 0) {
          this.agenciesProximas = this.calculatePotentialAgencies(agencies)
        }
      }

      this.setShippingData({
        tra_nome: carrier.TRA_NOME,
        valor: carrier.VALOR,
        prazo: carrier.PRAZO,
        peso: carrier.PESO
      })
    },
    
    async loadReverseCarriers(address) {
      if (address && address.cep) {
        this.cepClient = address.cep;
      }
      await this.fetchCarriersAndAgencies();
    },

    async fetchCarriersAndAgencies() {
      if (!this.cepClient) return
      const cleanCepToSearch = getOnlyNumbersFromString(this.cepClient)
      this.lastSearchedCep = cleanCepToSearch
      this.isLoading = true
      this.resetShippingData()
      this.selectedCarrier = {}
      this.agenciesCache = {}
      this.latitudeClient = ''
      this.longitudeClient = ''
      this.agenciesProximas = []
      let peso = 0;
      if (this.itens && this.itens.length > 0) {
        peso = parseFloat(
          this.itens.reduce((total, item) => {
            const peso = parseFloat(String(item.ite_pesobru).replace(',', '.')) || 0
            const qtd = parseFloat(item.qtdDevolucao) || 0
            return total + (peso * qtd)
          }, 0).toFixed(2)
        )
      }
      try {
        const searchData = {
          cep_origem: cleanCepToSearch,
          cep_destino: this.itens[0]?.fil_cep ? getOnlyNumbersFromString(this.itens[0]?.fil_cep) : '',
          pesoItens: peso.toFixed(2).replace('.', ','),
          valorItens: this.itens.reduce((total, item) => {
            const preco = parseFloat(String(item.produtoValor).replace(',', '.')) || 0
            const qtd = parseFloat(item.qtdDevolucao) || 0
            return total + (preco * qtd)
          }, 0).toFixed(2).replace('.', ','),
          marca: this.brand.mar_cods[0]
        }

        const isValid = Object.values(searchData).every(val => val) && peso > 0
        if (isValid) {
          const response = await this.loadCarriersReverse(searchData) || {}
          this.showCepInput = response?.parameterActive === 'S'
          this.carriers = (response.carriersReverseB2C || []).map(carrier => ({
            ...carrier,
            PESO: peso
          }))
        }
        const promisses = this.carriers.map(async (carrier) => {
          const carrierName = carrier.TRA_NOME
          const carrierNameUpper = carrierName ? carrierName.toUpperCase() : ''
          const requiresAgency = this.agencyCarriersConfig.some(configName => 
            carrierNameUpper.includes(configName)
          )
          if (requiresAgency) {
            const hasAgencies = await this.checkAgencyAvailability(carrierName)
            return hasAgencies ? carrier : null
          }
          return carrier
        })
        const carriersList = await Promise.all(promisses)
        this.carriers = carriersList.filter(carrier => carrier !== null)
      } catch (error) {
        this.$ShowAlert({
          variant: 'error',
          message: error,
          hideDelay: 2000
        })
        this.carriers = []
      } finally {
        const hasPac = this.carriers.some(c => c.TRA_NOME === 'PAC-CORREIOS')
        if (!hasPac) {
          this.carriers.push({
            TRA_NOME: "PAC-CORREIOS",
            VALOR: 0,
            PRAZO: 0,
            PESO: peso
          })
        }
        this.carriers.sort((a, b) => (b.TRA_NOME === 'PAC-CORREIOS') - (a.TRA_NOME === 'PAC-CORREIOS'))
        if (this.carriers.length && this.carriers.length > 0) {
          this.selectCarrier(this.carriers[0])
        }
        this.isLoading = false
      }
    },
    async checkAgencyAvailability(carrierName) {
      if (this.agenciesCache[carrierName] && this.agenciesCache[carrierName].length > 0) return true
      try {
        const response = await this.loadCarriersAgencies({ carrier: carrierName })
        this.rangeParam = response.range || 10
        const agenciesList = response.carriersAgencies || []
        await this.resolveClientLocation()
        if (this.latitudeClient && this.longitudeClient && agenciesList.length > 0) {
          const validAgencies = this.calculatePotentialAgencies(agenciesList)
          if (validAgencies.length > 0) {
            this.agenciesCache[carrierName] = agenciesList
            return true
          }
        }
        return false
      } catch (e) {
        return false
      }
    },

    async resolveClientLocation() {
      if (!this.cepClient) return
      if (this.latitudeClient && this.longitudeClient) return
      try {
        const data = await this.loadGeoCoordinates({ cep: getOnlyNumbersFromString(this.cepClient) })
        if (data && data.lat && data.lng) {
          this.latitudeClient = data.lat
          this.longitudeClient = data.lng
        }
      } catch (err) {
        throw err
      }
    },

    calcularDistancia(lat1, lon1, lat2, lon2) {
      const R = 6371
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLon = (lon2 - lon1) * Math.PI / 180
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    calculatePotentialAgencies(list) {
      if (!list) return []
      return list.map(agency => {
        const latStr = String(agency.END_LATIT || 0).replace(',', '.')
        const lonStr = String(agency.END_LOGIT || 0).replace(',', '.')
        const agLat = parseFloat(latStr)
        const agLon = parseFloat(lonStr)
        const distancia = this.calcularDistancia(
          this.latitudeClient, this.longitudeClient, agLat, agLon
        )

        return {
          ...agency,
          position: { lat: agLat, lon: agLon },
          code: agency.END_ID,
          name: agency.END_TRANOME,
          distanciaAprox: distancia
        }
      })
      .filter(a => a.distanciaAprox <= this.rangeParam)
      .sort((a, b) => a.distanciaAprox - b.distanciaAprox)
    },

    selectAgency(agency) {
      this.selectedAgency = agency
      this.updateShippingDataWithAgency()
    },

    updateShippingDataWithAgency() {
      if (!this.selectedAgency) return
      this.setShippingData({
        agencia: this.selectedAgency.code,
      })
    },

    formatDistance(km) {
      if (!km) return '0 m'
      if (km < 1) {
        const metros = Math.round(km * 1000)
        return `${metros} m`
      }
      return `${km.toFixed(1).replace('.', ',')} km`
    },

    validateDataRequired() {
      if (!this.selectedCarrier) {
        this.$ShowAlert({ 
            variant: 'warning', 
            message: this.$t('etapas.transporte.carrierRequired')
        })
        return false
      }
      if (this.selectedCarrierRequiresAgency && (!this.selectedAgency || !this.selectedAgency.code)) {
          this.$ShowAlert({ 
            variant: 'warning', 
            message: this.$t('etapas.transporte.agencyRequired')
          })
          return false
      }
      return true
    }
  },
  beforeUnmount() {    
    this.resetShippingData()
  },   
}
</script>

<style scoped lang="scss">
.dass-title-shipping {
  margin-bottom: 10px;
  > b {
    color: var(--color-status--success); 
  }
}

.shipping-step-section {
  margin-bottom: 25px;
}

.cep-step-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  .title-wrapper {
    flex: 1;
    min-width: 280px;
  }

  .step-title {
    margin-bottom: 0;
    width: 100%; 
  }
}

.step-title {
  margin-bottom: 12px;
  color: #333;
  font-weight: 500;
}

.cep-action-group {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap; 
  
  .cep-input-wrapper {
    width: 170px;
    margin-bottom: 0;
    :deep(.dass-form-control) {
      width: 100%;
    }

    :deep(.dass-input-text--input-wrapper) {
      margin-top: 0 !important;
    }
    @media (max-width: 576px) {
      width: 100%;
    }
  }

  .cep-btn {
    margin-top: 0;
    height: 45px;
    min-width: 110px;
    margin-bottom: var(--space__0-25x);
    @media (max-width: 576px) {
      width: 100%;
    }
  }

  @media (max-width: 576px) {
    width: 100%;
  }
}

.carriers-cards-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.carrier-card {
  display: flex;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    border-color: #adb5bd;
    box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  }

  &.selected {
    border-color: #28a745;
    background-color: #f1faee; 
  }

  .radio-wrapper {
    margin-right: 15px;
    margin-top: 2px;
    
    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #28a745; 
    }
  }

  .card-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      width: 100%;

      .title-with-icon {
        display: flex;
        align-items: center;
        gap: 8px;
      
        strong {
          font-size: 15px;
          color: #212529;
        }

        .carrier-small-icon {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
      }
    }
    
    .card-description {
      font-size: 13px;
      color: #6c757d;
      line-height: 1.4;
      margin: 0;
    }
  }
}

.loading-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.agencies-wrapper {
  margin-top: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  padding: 15px;
  animation: fadeIn 0.3s ease-in-out;
  .distance-disclaimer {
    display: block;
    text-align: left;
    font-size: 11px;
    color: #888;
    margin-top: 10px;
    border-top: 1px solid #e9ecef;
    padding-top: 8px;
  }
}

.agencies-header {
  margin-bottom: 12px;
  font-size: 14px;
  color: #495057;
}

.agencies-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.agency-card {
  display: flex;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #fff;
    border-color: #adb5bd;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  &.selected {
    border-color: var(--color-status--success, #28a745);
    background-color: #f0fff4;
  }
}

.agency-radio-col {
  padding-right: 10px;
  padding-top: 2px;
}

.agency-info-col {
  flex: 1;
  position: relative;
}

.info-text-wrapper {
  display: flex;
  flex-direction: column;
  padding-right: 80px;
}

.agency-name {
  display: block;
  font-size: 13px;
  color: #212529;
  margin-bottom: 2px;
}

.agency-address {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 6px;
  line-height: 0.8;
}

.agency-badges {
  position: absolute;
  top: 0;
  right: 0;
}

.badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;

  &.dist {
    background-color: #e2e3e5;
    color: #383d41;
  }
}

@keyframes fadeIn {
  from { 
    opacity: 0;
    transform: translateY(-5px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}
</style>